import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import katex from 'katex';
import { Layers, Plus, Trash2, Image as ImageIcon, Sparkles, ChevronDown, ArrowUp, ArrowDown, FileText } from 'lucide-react';

import { ExamPaper } from './types';
import { DiagramBlock } from '../../components/DiagramBlock';

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Check, X } from "lucide-react";
import { db, auth, storage } from "@/firebase/config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

type AssessmentType = 'CAT' | 'Assignment' | 'Exam' | 'Quiz' | 'Custom';
type QuestionType = 'Multiple Choice' | 'Short Answer' | 'Essay' | 'True/False' | 'Section';

interface SubQuestion {
  id: string;
  type: QuestionType; // Re-use the main QuestionType
  text: string;
  marks: number;
  options?: string[] | null; // For MCQ within sub-question
  correctAnswerIndex?: number; // Correct option index
  modelAnswer?: string | null; // Correct answer text or explanation
}

interface Question {
  id: string;
  type: QuestionType;
  text: string;
  marks: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  options?: string[] | null; // For MCQ
  correctAnswerIndex?: number; // Correct option index
  modelAnswer?: string | null;
  learningObjective?: string;
  image?: string | null; // For image attachments
  subQuestions?: SubQuestion[] | null;
  section?: string; // e.g. "Section A"
}

export interface ManualSectionLayout {
  id: number;
  name: string;
  qnCount: number;
}

export interface ManualAssessmentDraft {
  title: string;
  type: AssessmentType;
  examType: string;
  customExamName: string;
  topics: string[];
  instructions: string;
  duration: string;
  questions: Question[];
  sections: ManualSectionLayout[];
  structure: { name: string; count: number }[];
  totalMarks: number;
}

interface ManualEntryProps {
  mode: 'manual' | 'ai';
  setMode: (mode: 'manual' | 'ai') => void;
  examPaper: ExamPaper | null;
  setExamPaper: (paper: ExamPaper | null) => void;
  onBack: () => void;
  initialData?: ManualAssessmentDraft | null;
  onDraftChange?: (draft: ManualAssessmentDraft) => void;
}

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

const splitTextByDiagrams = (text: string) => {
  const regex = /(%%DIAGRAM%%.*?%%)/s;
  return text.split(regex).filter(Boolean);
};

const MarkdownText = ({ children, className }: { children?: string; className?: string }) => {
  const textToProcess = typeof children === 'string' ? children : String(children || '');
  const processedText = textToProcess
    .replace(/\\\(([\s\S]*?)\\\)/g, (match) => '$' + match.slice(2, -2) + '$')
    .replace(/\\\[([\s\S]*?)\\\]/g, (match) => '$$' + match.slice(2, -2) + '$$');

  const parts = splitTextByDiagrams(processedText);

  return (
    <div className={`markdown-body ${className || ''}`}>
      {parts.map((part, index) => {
        if (part.startsWith('%%DIAGRAM%%') && part.endsWith('%%')) {
          try {
            const jsonStr = part.slice(11, -2).trim();
            const spec = JSON.parse(jsonStr);
            return (
              <div key={index} className="flex justify-center my-4">
                <div className="max-w-[400px] w-full">
                  <DiagramBlock spec={spec} />
                </div>
              </div>
            );
          } catch {
            return <div key={index} className="text-red-500 text-xs my-2">[Diagram Parse Error]</div>;
          }
        }
        return (
          <ReactMarkdown key={index} remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
            {part}
          </ReactMarkdown>
        );
      })}
    </div>
  );
};

const renderMathInHtml = (html: string): string => {
  if (!html) return '';
  let result = html.replace(/\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g, (_m, g1, g2) => {
    try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
    catch { return _m; }
  });
  result = result.replace(/(?<!\$)\$(?!\$)([^$\n]+?)\$(?!\$)|\\\(([\s\S]+?)\\\)/g, (_m, g1, g2) => {
    try { return katex.renderToString((g1 ?? g2).trim(), { displayMode: false, throwOnError: false }); }
    catch { return _m; }
  });
  return result;
};

const parseModelAnswerContent = (text?: string | null) => {
  const content = String(text || '').trim();
  if (!content) {
    return { answer: '', steps: [] as string[] };
  }

  const parts = content
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);

  let answer = '';
  let steps = [] as string[];

  if (parts.length > 0) {
    if (/^Answer:/i.test(parts[0])) {
      answer = parts[0].replace(/^Answer:\s*/i, '').trim();
      steps = parts.slice(1);
    } else if (!/^Step\s+\d+:/i.test(parts[0])) {
      answer = parts[0].trim();
      steps = parts.slice(1);
    } else {
      steps = parts;
    }
  }

  steps = steps.map((step) => step.replace(/^Step\s+\d+:\s*/i, '').trim()).filter(Boolean);
  return { answer, steps };
};

const createQuestionId = () => Math.random().toString(36).substr(2, 9);

const createDefaultQuestion = (): Question => ({
  id: '1',
  type: 'Multiple Choice',
  text: '',
  marks: 2,
  difficulty: 'Easy',
  options: ['', '', '', ''],
  learningObjective: '',
  section: 'Section A'
});

const normalizeExamType = (value?: string) => {
  if (value === 'Exam') return 'MAIN EXAM';
  if (value === 'Custom') return 'CUSTOM';
  return value || 'CAT';
};

const mapExamTypeToAssessmentType = (value: string): AssessmentType => {
  if (value === 'MAIN EXAM') return 'Exam';
  if (value === 'CUSTOM') return 'Custom';
  if (value === 'Assignment' || value === 'Exam' || value === 'Quiz' || value === 'Custom') return value;
  return 'CAT';
};

export function ManualEntry({ mode, setMode, examPaper, setExamPaper, onBack, initialData, onDraftChange }: ManualEntryProps) {
  
  const [questions, setQuestions] = useState<Question[]>(initialData?.questions?.length ? initialData.questions : [createDefaultQuestion()]);
  const [activeImageUploadId, setActiveImageUploadId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // --- HANDLERS ---
  const addQuestion = () => {
    const newQ: Question = {
      id: createQuestionId(),
      type: 'Short Answer',
      text: '',
      marks: 2,
      difficulty: 'Medium',
      learningObjective: '',
      section: 'Section A'
    };
    setQuestions([...questions, newQ]);
  };


  const addSubQuestion = (qId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId) {
           const newSub: SubQuestion = {
              id: createQuestionId(),
              type: 'Short Answer',
              text: '',
              marks: 1,
              options: ['', '', '', ''] // Default options if they switch to MCQ
           };
           const newSubQuestions = [...(q.subQuestions || []), newSub];
           // Auto-calc total marks
           const newTotal = newSubQuestions.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0);
           
           return { 
               ...q, 
               marks: newTotal,
               subQuestions: newSubQuestions 
           };
        }
        return q;
     }));
  };

  const updateSubQuestion = (qId: string, subId: string, field: keyof SubQuestion, value: any) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           const updatedSubs = q.subQuestions.map(sq => sq.id === subId ? { ...sq, [field]: value } : sq);
           
           // Recalculate parent marks if we changed marks
           let newMarks = q.marks;
           if (field === 'marks') {
               newMarks = updatedSubs.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0);
           }
           
           return {
              ...q,
              marks: newMarks,
              subQuestions: updatedSubs
           };
        }
        return q;
     }));
  };

  const removeSubQuestion = (qId: string, subId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           const remainingSubs = q.subQuestions.filter(sq => sq.id !== subId);
           // Recalculate
           const newMarks = remainingSubs.length > 0 
                ? remainingSubs.reduce((sum, sq) => sum + (Number(sq.marks)||0), 0)
                : 2; // Default back to 2 if no subquestions left
                
           return { ...q, marks: newMarks, subQuestions: remainingSubs };
        }
        return q;
     }));
  };

  // Sub-Question Option Helpers
  const addSubOption = (qId: string, subId: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId) {
                    return { ...sq, options: [...(sq.options || []), ''] };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const updateSubOption = (qId: string, subId: string, optIndex: number, value: string) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId && sq.options) {
                    const newOpts = [...sq.options];
                    newOpts[optIndex] = value;
                    return { ...sq, options: newOpts };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const removeSubOption = (qId: string, subId: string, optIndex: number) => {
     setQuestions(questions.map(q => {
        if (q.id === qId && q.subQuestions) {
           return {
              ...q,
              subQuestions: q.subQuestions.map(sq => {
                 if (sq.id === subId && sq.options && sq.options.length > 2) {
                    return { ...sq, options: sq.options.filter((_, i) => i !== optIndex) };
                 }
                 return sq;
              })
           };
        }
        return q;
     }));
  };

  const removeQuestion = (id: string) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const updateQuestion = (id: string, field: keyof Question, value: any) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, [field]: value } : q));
  };

  const moveQuestion = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === questions.length - 1) return;
    
    const newQuestions = [...questions];
    const temp = newQuestions[index];
    newQuestions[index] = newQuestions[index + (direction === 'up' ? -1 : 1)];
    newQuestions[index + (direction === 'up' ? -1 : 1)] = temp;
    setQuestions(newQuestions);
  };

  // MCQ Helpers
  const addOption = (qId: string) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options) {
        return { ...q, options: [...q.options, ''] };
      }
      return q;
    }));
  };

  const removeOption = (qId: string, optIndex: number) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options && q.options.length > 2) {
        return { ...q, options: q.options.filter((_, i) => i !== optIndex) };
      }
      return q;
    }));
  };

  const updateOption = (qId: string, optIndex: number, value: string) => {
    setQuestions(questions.map(q => {
      if (q.id === qId && q.options) {
        const newOpts = [...q.options];
        newOpts[optIndex] = value;
        return { ...q, options: newOpts };
      }
      return q;
    }));
  };

  // Image Helper
  const triggerImageUpload = (qId: string) => {
    setActiveImageUploadId(qId);
    fileInputRef.current?.click();
  };

  const handleImageFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && activeImageUploadId && auth.currentUser) {
      try {
          const storageRef = ref(storage, `teaching_workspaces/${auth.currentUser.uid}/assessment_images/${activeImageUploadId}_${Date.now()}`);
          const snapshot = await uploadBytes(storageRef, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          
          updateQuestion(activeImageUploadId, 'image', downloadURL);
          setActiveImageUploadId(null);
          // Optional: Show success feedback
      } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
      }
    }
  };

  // --- MOCK DATA HANDLERS ---
  // Helper function for sanitization
  const stripHtml = (html: string) => {
      if (!html) return "";
      return html.replace(/<[^>]*>?/gm, '');
  };

  // Format model answer for Mark Scheme: Answer in orange, Explanation in green
  const formatMarkSchemeAnswer = (text: string | null | undefined): string => {
      if (!text) return '<span class="text-gray-400 italic">No model answer provided.</span>';
      
      let content = String(text).trim();
      
      if (!content) {
          return '<span class="text-gray-400 italic">No model answer provided.</span>';
      }
      
      // Split content into answer and explanation parts
      // Look for first "Step" to separate answer from explanation
      const stepMatch = content.match(/Step\s+\d+/i);
      let answerPart = '';
      let explanationPart = '';

        const labeledAnswerMatch = content.match(/^Answer:\s*([\s\S]*?)(?:\n{2,}|$)/i);
        if (labeledAnswerMatch) {
          answerPart = labeledAnswerMatch[1].trim();
          explanationPart = content.slice(labeledAnswerMatch[0].length).trim();
        } else 
      
        if (stepMatch && stepMatch.index !== undefined && stepMatch.index > 0) {
          answerPart = content.substring(0, stepMatch.index).trim();
          explanationPart = content.substring(stepMatch.index).trim();
      } else if (stepMatch && stepMatch.index === 0) {
          // Content starts with Step - try to find a final result in the steps
          // Look for patterns like "Final:", "Result:", "Answer:", "Therefore:", "dy/dx =", "= " at end
          const finalMatch = content.match(/(?:Final|Result|Answer|Therefore|Hence)[:\s]+([^\n]+)/i) 
                          || content.match(/(?:dy\/dx|f'?\(x\)|y')\s*=\s*([^\n\[\(]+)/i);
          if (finalMatch && finalMatch[1]) {
              answerPart = finalMatch[1].replace(/\[.*marks.*\]/i, '').trim();
          } else {
              answerPart = 'See steps below';
          }
          explanationPart = content;
      } else {
          // No steps found - treat first line as answer, rest as explanation
          const lines = content.split(/\n+/).filter(l => l.trim());
          answerPart = lines[0] || '';
          explanationPart = lines.slice(1).join('\n');
      }
      
      let html = '';
      
      // Answer in orange box
      if (answerPart) {
          html += `<div class="text-orange-600 dark:text-orange-400 font-semibold p-2 bg-orange-50 dark:bg-orange-900/20 rounded border-l-4 border-orange-400 mb-2">${answerPart}</div>`;
      }
      
      // Explanation in green box - split by "Step X" to put each on new line
      if (explanationPart) {
          // Split by Step patterns and format each step on its own line
          const stepParts = explanationPart.split(/(Step\s+\d+:?)/i).filter(s => s.trim());
          let formattedSteps: string[] = [];
          
          for (let i = 0; i < stepParts.length; i++) {
              const part = stepParts[i].trim();
              if (/^Step\s+\d+:?$/i.test(part) && stepParts[i + 1]) {
                  // This is a step header, combine with next part
                  formattedSteps.push(`<span class="font-bold text-green-700 dark:text-green-300">${part}</span> ${stepParts[i + 1].trim()}`);
                  i++; // Skip next part since we combined it
              } else if (!/^Step\s+\d+:?$/i.test(part)) {
                  formattedSteps.push(part);
              }
          }
          
          const explanationHtml = formattedSteps.join('<br/><br/>');
          html += `<div class="text-green-800 dark:text-green-300 text-sm p-2 bg-green-50 dark:bg-green-900/20 rounded border-l-4 border-green-400">${explanationHtml}</div>`;
      }
      
      // If nothing was generated, just show content in orange
      if (!html) {
          html = `<div class="text-orange-600 dark:text-orange-400 font-medium">${content}</div>`;
      }
      
      return html;
  };



    const [examType, setExamType] = useState(normalizeExamType(initialData?.examType || initialData?.type));
    const [customExamName, setCustomExamName] = useState(initialData?.customExamName || '');
    const [topics, setTopics] = useState<string[]>(initialData?.topics || []);
  const [currentTopic, setCurrentTopic] = useState('');
    const [sections, setSections] = useState<ManualSectionLayout[]>(initialData?.sections?.length ? initialData.sections : [{ id: 1, name: 'Section A', qnCount: 1 }]);
    const [duration, setDuration] = useState(initialData?.duration || '40 Mins');
    const [instructions, setInstructions] = useState(initialData?.instructions || 'Answer all questions in the spaces provided.');

  const handleAddTopic = () => {
    if (currentTopic.trim() && !topics.includes(currentTopic.trim())) {
      setTopics([...topics, currentTopic.trim()]);
      setCurrentTopic('');
    }
  };

  const handleRemoveTopic = (topicToRemove: string) => {
    setTopics(topics.filter(t => t !== topicToRemove));
  };

  const addSection = () => {
    const newId = sections.length > 0 ? Math.max(...sections.map(s => s.id)) + 1 : 1;
    const nextLetter = String.fromCharCode(65 + sections.length);
    setSections([...sections, { id: newId, name: `Section ${nextLetter}`, qnCount: 1 }]);
  };

  const removeSection = (idToRemove: number) => {
    if (sections.length > 1) {
      setSections(sections.filter(s => s.id !== idToRemove));
    }
  };

  const updateSection = (index: number, field: string, value: any) => {
    const newSections = [...sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setSections(newSections);
  };

  const questionsWithSections = useMemo(() => {
    const safeSections = sections.length > 0 ? sections : [{ id: 1, name: 'Section A', qnCount: Math.max(questions.length, 1) }];
    return questions.map((question, index) => {
      let assignedSection = safeSections[safeSections.length - 1]?.name || 'Section A';
      let cumulative = 0;

      for (const section of safeSections) {
        cumulative += Math.max(0, Number(section.qnCount) || 0);
        if (index < cumulative) {
          assignedSection = section.name;
          break;
        }
      }

      return {
        ...question,
        section: assignedSection,
      };
    });
  }, [questions, sections]);

  const totalMarks = useMemo(() => questionsWithSections.reduce((sum, q) => {
    const qMarks = Number(q.marks) || 0;
    const subMarks = q.subQuestions?.reduce((s, sq) => s + (Number(sq.marks) || 0), 0) || 0;
    return sum + (q.subQuestions && q.subQuestions.length > 0 ? subMarks : qMarks);
  }, 0), [questionsWithSections]);

  const derivedStructure = useMemo(() => {
    const sectionCounts = new Map<string, number>();
    questionsWithSections.forEach((question) => {
      const sectionName = question.section || 'Section A';
      sectionCounts.set(sectionName, (sectionCounts.get(sectionName) || 0) + 1);
    });

    if (sectionCounts.size === 0) {
      return [{ name: 'Section A', count: 0 }];
    }

    return Array.from(sectionCounts.entries()).map(([name, count]) => ({ name, count }));
  }, [questionsWithSections]);

  const derivedTitle = useMemo(() => {
    if (customExamName.trim()) return customExamName.trim();
    if (topics.length > 0) return `${examType === 'CUSTOM' ? 'Custom Assessment' : examType}: ${topics.join(', ')}`;
    return examType === 'CUSTOM' ? 'Custom Assessment' : `${examType} Assessment`;
  }, [customExamName, examType, topics]);

  useEffect(() => {
    onDraftChange?.({
      title: derivedTitle,
      type: mapExamTypeToAssessmentType(examType),
      examType,
      customExamName,
      topics,
      instructions,
      duration,
      questions: questionsWithSections,
      sections,
      structure: derivedStructure,
      totalMarks,
    });
  }, [customExamName, derivedStructure, derivedTitle, duration, examType, instructions, onDraftChange, questionsWithSections, sections, topics, totalMarks]);



  return (
    <div className="space-y-6">
      <Input type="file" className="hidden" ref={fileInputRef} accept="image/*" onChange={handleImageFile} />
      {/* Section 1: Assessment Configuration */}
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
           <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between items-center gap-3">
               <CardTitle className="flex items-center gap-2">
                 <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">1</span>
                 Assessment Configuration
               </CardTitle>
               
               <div className="flex items-center gap-2 bg-gray-100 dark:bg-white/5 p-1 rounded-lg">
                   <button
                      onClick={() => setMode('manual')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${mode === 'manual' ? 'bg-white dark:bg-gray-800 shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                   >
                       Manual Entry
                   </button>
                   <button
                      onClick={() => setMode('ai')}
                      className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all flex items-center gap-1.5 ${mode === 'ai' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                   >
                       <Sparkles className="w-3 h-3" /> Generate with AI
                   </button>
               </div>
           </div>
           <CardDescription>Select the type of validity and core details.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 animate-in fade-in slide-in-from-top-2 duration-300 px-2 sm:px-6">

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Topic(s)</label>
            <div className="flex gap-2 mb-2">
              <input 
                type="text" 
                value={currentTopic}
                onChange={(e) => setCurrentTopic(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTopic();
                  }
                }}
                placeholder="Type a topic and press Enter or click Add"
                className="flex-1 bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-600 focus:outline-none focus:border-blue-500 transition-colors"
              />
              <button 
                type="button" 
                onClick={handleAddTopic}
                className="px-3 py-2 bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-white/10 transition-colors"
              >
                Add
              </button>
            </div>
            {topics.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {topics.map(t => (
                  <span key={t} className="bg-blue-50 dark:bg-white/10 border border-gray-200 dark:border-white/20 text-gray-700 dark:text-gray-300 px-2.5 py-1 rounded-md text-xs flex items-center gap-1.5">
                    {t}
                    <button 
                      type="button" 
                      onClick={() => handleRemoveTopic(t)}
                      className="text-muted-foreground hover:text-red-400 transition-colors"
                    >
                      &times;
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Assessment Type</label>
            <div className="relative">
              <select 
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                title="Assessment type"
                className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 appearance-none focus:outline-none focus:border-blue-500 transition-colors"
              >
                <option value="CAT">CAT</option>
                <option value="MAIN EXAM">MAIN EXAM</option>
                <option value="CUSTOM">CUSTOM</option>
              </select>
              <ChevronDown className="w-3.5 h-3.5 absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
          </div>
        </div>

        {examType === 'CUSTOM' && (
          <div className="mb-5">
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Custom Exam Name</label>
            <input 
              type="text" 
              value={customExamName}
              onChange={(e) => setCustomExamName(e.target.value)}
              placeholder="e.g. Mid-Term 2 Mock"
              className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        )}

        <div className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-900 dark:text-gray-100">
              <Layers className="w-3.5 h-3.5 text-blue-500" />
              Assessment Structure & Layout
            </div>
            <button 
              onClick={addSection}
              title="Add section"
              className="flex items-center gap-1 text-xs font-medium text-blue-500 hover:text-blue-400 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Section
            </button>
          </div>
          
          <div className="space-y-3">
            {sections.map((section, index) => (
              <div key={section.id} className="flex flex-wrap items-end gap-2 sm:gap-3">
                <div className="flex-1 min-w-[140px]">
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Section Name</label>
                  <input 
                    type="text" 
                    value={section.name}
                    onChange={(e) => updateSection(index, 'name', e.target.value)}
                    className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <div className="w-24">
                  <label className="block text-[10px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">QN Count</label>
                  <input 
                    type="number" 
                    value={section.qnCount}
                    onChange={(e) => updateSection(index, 'qnCount', parseInt(e.target.value) || 0)}
                    className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
                  />
                </div>
                <button 
                  onClick={() => removeSection(section.id)}
                  title={`Remove ${section.name}`}
                  className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors mb-0.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        </CardContent>
      </Card>

      {/* Section 2: Instructions & Guidelines */}
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
              2
            </div>
            <CardTitle>Instructions & Guidelines</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 animate-in fade-in duration-300 px-2 sm:px-6">

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
          <div className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg p-3">
            <label className="block text-[10px] font-medium text-muted-foreground mb-1 uppercase tracking-wider">Total Marks</label>
            <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{totalMarks}</div>
          </div>
          <div className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg p-3">
            <label className="block text-[10px] font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">Est. Duration</label>
            <input 
              type="text" 
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              className="w-full bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-md px-2.5 py-1 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1.5">Student-Facing Instructions</label>
          <textarea 
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            rows={2}
            className="w-full bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 transition-colors resize-none font-mono text-xs"
          />
        </div>
        </CardContent>
      </Card>

      
      {/* Section 3: Question Builder */}
      <Card>
        <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 flex flex-row items-center justify-between space-y-0 p-2 sm:p-6">
          <div className="flex items-center gap-3 mt-1">
            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold text-xs">
              3
            </div>
            <CardTitle>Question Builder</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 pt-6 animate-in fade-in duration-300 px-2 sm:px-6">
            {questionsWithSections.map((q, idx) => {
             // Check if this is the first question of a new section
             const isSectionStart = idx === 0 || questions[idx - 1].section !== q.section;

             return (
               <div key={q.id}>
                 {isSectionStart && q.section && (
                    <div className="mb-6 mt-8 first:mt-0 flex items-center gap-4">
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                        <span className="px-4 py-1.5 bg-gray-100 dark:bg-white/10 rounded-full text-sm font-bold text-gray-600 dark:text-gray-300 shadow-sm border border-gray-200 dark:border-white/5">
                            {q.section}
                        </span>
                        <div className="h-px flex-1 bg-gray-200 dark:bg-white/10" />
                    </div>
                 )}
             <div key={q.id} className="relative group border rounded-xl p-4 sm:p-6 overflow-hidden hover:shadow-md transition-shadow bg-white dark:bg-[#0f1221]">
                {/* Header Row */}
                <div className="flex gap-3 sm:gap-4 items-start mb-4">
                   <div className="flex flex-col items-center gap-1 pt-1 shrink-0">
                     <span className="font-bold text-lg text-muted-foreground/50">Q{idx + 1}</span>
                     <GripVertical className="w-4 h-4 text-muted-foreground/30 cursor-grab" />
                   </div>
                   
                   <div className="flex-1 min-w-0 grid md:grid-cols-4 gap-4">
                      <div className="md:col-span-3">
                         <Textarea 
                           placeholder="Type your question here..." 
                           className="min-h-[84px] font-medium text-base border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 resize-y"
                           value={q.text}
                           onChange={(e) => updateQuestion(q.id, 'text', e.target.value)}
                         />
                         {q.text?.trim() && (
                           <div className="mt-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2">
                             <Label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Rendered preview</Label>
                             <MarkdownText className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{q.text}</MarkdownText>
                           </div>
                         )}
                         
                         {/* Image Attachment Area */}
                         {/* Image Attachment Area */}
                         <div className="mt-2 text-xs flex flex-wrap gap-2 sm:gap-4 items-center">
                            {q.image ? (
                                <div className="relative inline-block mt-2">
                                    <img src={q.image} alt="Question Attachment" className="h-32 rounded-lg border border-gray-200" />
                                    <Button 
                                        size="icon" 
                                        variant="destructive" 
                                        className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                                        onClick={() => updateQuestion(q.id, 'image', null)}
                                    >
                                        <X className="w-3 h-3" />
                                    </Button>
                                </div>
                            ) : (
                                <Button 
                                    variant="link" 
                                    className="p-0 h-auto text-muted-foreground hover:text-primary flex items-center gap-1"
                                    onClick={() => triggerImageUpload(q.id)}
                                >
                                    <ImageIcon className="w-3 h-3" /> Attach Image/Start Diagram
                                </Button>
                            )}
                            
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs border-dashed text-muted-foreground hover:text-primary hover:border-primary/50"
                                onClick={() => addSubQuestion(q.id)}
                            >
                                <Plus className="w-3 h-3 mr-1" /> Add Sub-part (a, b...)
                            </Button>
                         </div>
                      </div>
                      <div>
                         <Select 
                           value={q.type} 
                           onValueChange={(v) => updateQuestion(q.id, 'type', v)}
                         >
                           <SelectTrigger className="h-8 text-[11px] font-bold bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 hover:border-primary/30 transition-all rounded-lg uppercase tracking-wider">
                              <SelectValue />
                           </SelectTrigger>
                           <SelectContent className="bg-white dark:bg-[#0f1221] border-gray-100 dark:border-white/10 shadow-xl p-1">
                              <SelectItem value="Multiple Choice" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Multiple Choice</SelectItem>
                              <SelectItem value="Short Answer" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Short Answer</SelectItem>
                              <SelectItem value="Essay" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">Essay</SelectItem>
                              <SelectItem value="True/False" className="focus:bg-primary/5 dark:focus:bg-primary/10 cursor-pointer text-xs">True / False</SelectItem>
                           </SelectContent>
                         </Select>
                      </div>
                   </div>
                   
                   {/* Actions */}
                   <div className="flex flex-col gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => moveQuestion(idx, 'up')}>
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-primary" onClick={() => moveQuestion(idx, 'down')}>
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-red-400 hover:text-red-500 hover:bg-red-50" onClick={() => removeQuestion(q.id)}>
                          <Trash2 className="w-3 h-3" />
                      </Button>
                   </div>
                </div>

                 {/* Dynamic Content Based on Type */}
                 <div className="pl-0 sm:pl-10 space-y-4">
                   {/* SHOW OPTIONS/ANSWER ONLY IF NO SUB-QUESTIONS (Standard Mode) */}
                   {(!q.subQuestions || q.subQuestions.length === 0) && (
                       <>
                           {q.type === 'Multiple Choice' && (
                              <div className="space-y-3">
                                 <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    {q.options?.map((opt, i) => (
                                      <div key={i} className="flex items-center gap-2 group/opt">
                                         <div 
                                            className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] font-bold shrink-0 cursor-pointer transition-colors ${q.correctAnswerIndex === i ? 'bg-green-500 border-green-500 text-white' : 'border-gray-300 text-muted-foreground hover:border-green-400 hover:text-green-500'}`}
                                            onClick={() => updateQuestion(q.id, 'correctAnswerIndex', i)}
                                            title="Mark as Correct Answer"
                                         >
                                            {q.correctAnswerIndex === i ? <Check className="w-3 h-3" /> : String.fromCharCode(65+i)}
                                         </div>
                                         <Input 
                                            placeholder={`Option ${String.fromCharCode(65+i)}`} 
                                            className="h-9 text-sm" 
                                            value={opt}
                                            onChange={(e) => updateOption(q.id, i, e.target.value)}
                                         />
                                         {opt?.trim() && (
                                           <div className="hidden">
                                             <MarkdownText>{opt}</MarkdownText>
                                           </div>
                                         )}
                                         <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            className="h-6 w-6 opacity-0 group-hover/opt:opacity-100 text-red-400 hover:bg-red-50"
                                            onClick={() => removeOption(q.id, i)}
                                            disabled={q.options?.length ? q.options.length <= 2 : true}
                                         >
                                            <X className="w-3 h-3" />
                                         </Button>
                                      </div>
                                    ))}
                                 </div>
                                 <Button variant="ghost" size="sm" className="text-xs h-6 px-2 text-primary hover:bg-primary/5" onClick={() => addOption(q.id)}>
                                    <Plus className="w-3 h-3 mr-1" /> Add Option
                                 </Button>
                              </div>
                           )}
                           
                           {q.type !== 'Multiple Choice' && (!q.subQuestions || q.subQuestions.length === 0) && (
                              <div className="p-3 bg-gray-50 dark:bg-white/5 rounded-md">
                                 <Label className="text-xs text-muted-foreground uppercase tracking-widest mb-1 block">Marking Guide / Model Answer</Label>
                                 <Textarea 
                                   placeholder="Key points expected in the answer..." 
                                   className="h-16 text-sm resize-none border-none bg-transparent focus-visible:ring-0 px-0" 
                                   value={q.modelAnswer || ''}
                                   onChange={(e) => updateQuestion(q.id, 'modelAnswer', e.target.value)}
                                 />
                                 {q.modelAnswer?.trim() && (
                                   <div className="mt-2" dangerouslySetInnerHTML={{ __html: renderMathInHtml(formatMarkSchemeAnswer(q.modelAnswer)) }} />
                                 )}
                              </div>
                           )}
                       </>
                   )}

                    {/* Sub-Questions Area (Multipart) */}
                    {q.subQuestions && q.subQuestions.length > 0 && (
                       <div className="space-y-3 pl-2 sm:pl-4 border-l-2 border-primary/20 mt-4">
                          <Label className="text-xs text-muted-foreground uppercase tracking-widest font-bold">Sub-Questions</Label>
                          {q.subQuestions.map((sq, sqIdx) => (
                              <div key={sq.id} className="flex flex-col gap-3 p-3 sm:p-4 border border-gray-100 dark:border-white/5 rounded-xl bg-gray-50/50 dark:bg-white/5 group/sub overflow-hidden">
                                  {/* Sub-Question Header Line */}
                                  <div className="flex gap-3 items-start">
                                      <div className="w-8 h-8 flex items-center justify-center font-bold text-sm text-primary bg-white dark:bg-white/10 shadow-sm rounded border border-gray-200 dark:border-white/10 shrink-0">
                                          {String.fromCharCode(97 + sqIdx)}
                                      </div>
                                      <div className="flex-1 min-w-0 space-y-3">
                                          <div className="flex flex-wrap gap-2 items-start">
                                              <div className="flex-1 min-w-[180px]">
                                                <Textarea 
                                                    value={sq.text} 
                                                    onChange={(e) => updateSubQuestion(q.id, sq.id, 'text', e.target.value)}
                                                    className="min-h-[72px] text-sm bg-white dark:bg-black/20 font-medium resize-y"
                                                    placeholder={`Part (${String.fromCharCode(97 + sqIdx)}) question text...`}
                                                />
                                                {sq.text?.trim() && (
                                                  <div className="mt-2 rounded-lg bg-white dark:bg-white/5 border border-gray-200 dark:border-white/10 px-3 py-2">
                                                    <Label className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1 block">Rendered preview</Label>
                                                    <MarkdownText className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{sq.text}</MarkdownText>
                                                  </div>
                                                )}
                                              </div>
                                              
                                              {/* Sub-Question Type Selector */}
                                              <Select 
                                                 value={sq.type} 
                                                 onValueChange={(v: any) => updateSubQuestion(q.id, sq.id, 'type', v)}
                                              >
                                                <SelectTrigger className="w-[130px] h-8 text-[10px] font-bold bg-white dark:bg-white/5 uppercase tracking-wider">
                                                   <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                   <SelectItem value="Short Answer">Short Answer</SelectItem>
                                                   <SelectItem value="Multiple Choice">Multiple Choice</SelectItem>
                                                </SelectContent>
                                              </Select>

                                              <div className="w-24 relative">
                                                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground font-bold">mks</span>
                                                  <Input 
                                                      type="number" 
                                                      value={sq.marks} 
                                                      onChange={(e) => updateSubQuestion(q.id, sq.id, 'marks', Number(e.target.value))}
                                                      className="h-8 text-sm pr-9 text-right font-bold bg-white dark:bg-black/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                  />
                                              </div>
                                              
                                              <Button 
                                                  size="icon" 
                                                  variant="ghost" 
                                                  className="h-8 w-8 text-red-400 opacity-0 group-hover/sub:opacity-100 transition-opacity hover:bg-red-50"
                                                  onClick={() => removeSubQuestion(q.id, sq.id)}
                                              >
                                                  <Trash2 className="w-3.5 h-3.5" />
                                              </Button>
                                          </div>

                                          {/* Sub-Question Options (If Multiple Choice) */}
                                          {sq.type === 'Multiple Choice' && (
                                              <div className="space-y-2 pl-2 border-l-2 border-dashed border-gray-200 dark:border-gray-700 ml-1">
                                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                       {(sq.options || ['', '', '', '']).map((opt, optI) => (
                                                           <div key={optI} className="flex items-center gap-2 min-w-0">
                                                              <div 
                                                                 className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold cursor-pointer transition-colors ${sq.correctAnswerIndex === optI ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-white/20 hover:bg-green-200 text-muted-foreground'}`}
                                                                 onClick={() => updateSubQuestion(q.id, sq.id, 'correctAnswerIndex', optI)}
                                                                 title="Mark as Correct Answer"
                                                              >
                                                                  {sq.correctAnswerIndex === optI ? <Check className="w-2.5 h-2.5" /> : String.fromCharCode(65 + optI)}
                                                              </div>
                                                              <Input 
                                                                 value={opt} 
                                                                 onChange={(e) => updateSubOption(q.id, sq.id, optI, e.target.value)}
                                                                 className="h-7 text-xs bg-white dark:bg-black/20"
                                                                 placeholder={`Option ${String.fromCharCode(65 + optI)}`} 
                                                              />
                                                              <Button
                                                                 size="icon" variant="ghost" className="h-6 w-6 text-red-300 hover:text-red-500"
                                                                 onClick={() => removeSubOption(q.id, sq.id, optI)}
                                                              >
                                                                 <X className="w-3 h-3" />
                                                              </Button>
                                                          </div>
                                                      ))}
                                                  </div>
                                                  <Button variant="ghost" size="sm" className="h-6 text-[10px] text-blue-600" onClick={() => addSubOption(q.id, sq.id)}>
                                                      <Plus className="w-3 h-3 mr-1" /> Add Option
                                                  </Button>
                                              </div>
                                          )}
                                      </div>
                                  </div>
                              </div>
                          ))}
                      </div>
                   )}

                   {/* Question Metadata Row */}
                   <div className="flex flex-wrap gap-4 pt-4 border-t border-dashed mt-4">
                      <div className="flex items-center gap-2">
                         <Label className="text-xs">Marks:</Label>
                         <Input 
                            type="number" 
                            className="w-16 h-8 disabled:opacity-70 disabled:bg-gray-100 dark:disabled:bg-white/10" 
                            value={q.marks} 
                            onChange={(e) => updateQuestion(q.id, 'marks', e.target.value)}
                            disabled={!!(q.subQuestions && q.subQuestions.length > 0)}
                            title={q.subQuestions && q.subQuestions.length > 0 ? "Auto-calculated from sub-questions" : "Set marks"}
                         />
                      </div>
                      <div className="flex-1">
                         <Input 
                            placeholder="Learning Objective (e.g. Analysis)" 
                            className="h-8 text-xs border-indigo-100 bg-indigo-50/30 text-indigo-700 placeholder:text-indigo-300" 
                            value={q.learningObjective}
                            onChange={(e) => updateQuestion(q.id, 'learningObjective', e.target.value)}
                         />
                      </div>
                       <Select 
                          value={q.difficulty}
                          onValueChange={(v) => updateQuestion(q.id, 'difficulty', v)}
                       >
                           <SelectTrigger className="w-full sm:w-[100px] h-8 text-[10px] bg-white dark:bg-white/5 border-gray-100 dark:border-white/10 hover:border-primary/30 transition-all rounded-lg font-bold uppercase tracking-tight">
                             <SelectValue />
                           </SelectTrigger>
                           <SelectContent className="bg-white dark:bg-[#0f1221] border-gray-100 dark:border-white/10 shadow-xl p-1">
                              <SelectItem value="Easy" className="focus:bg-green-500/10 dark:focus:bg-green-500/20 cursor-pointer text-[10px] font-bold text-green-600">EASY</SelectItem>
                              <SelectItem value="Medium" className="focus:bg-amber-500/10 dark:focus:bg-amber-500/20 cursor-pointer text-[10px] font-bold text-amber-600">MEDIUM</SelectItem>
                              <SelectItem value="Hard" className="focus:bg-red-500/10 dark:focus:bg-red-500/20 cursor-pointer text-[10px] font-bold text-red-600">HARD</SelectItem>
                           </SelectContent>
                        </Select>
                   </div>
                   
                   <div className="flex items-center gap-2 text-[10px] text-muted-foreground font-medium">
                      <Sparkles className="w-3 h-3 text-amber-500" />
                      <span>{q.difficulty} level question - aligned to syllabus.</span>
                   </div>
                </div>
             </div>
             </div>
             );
           })}
           
            <div className="grid grid-cols-1 gap-4">
                <Button 
                   variant="ghost" 
                   className="w-full h-32 rounded-2xl border-2 border-dashed border-gray-100 dark:border-white/5 hover:border-primary/40 hover:bg-primary/5 hover:text-primary transition-all duration-300 group/addbtn flex flex-col items-center justify-center gap-3" 
                   onClick={addQuestion}
                >
                   <div className="w-12 h-12 rounded-full bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 flex items-center justify-center transition-colors duration-300 shadow-inner">
                      <Plus className="w-6 h-6 group-hover/addbtn:rotate-90 transition-transform duration-500" />
                   </div>
                   <div className="text-center">
                      <span className="font-bold text-sm uppercase tracking-widest block">Add Next Question</span>
                   </div>
                </Button>
            </div>
        
        </CardContent>
      </Card>

{/* 4. MARKING & RUBRIC */}
      <Card>
         <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
           <CardTitle className="flex items-center gap-2">
             <span className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold">4</span>
             Mark Scheme & Rubric
           </CardTitle>
           <CardDescription>Auto-generated breakdown of expectations.</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 px-2 sm:px-6">
            <div className="space-y-4">
               {questions.filter(q => q.type !== 'Section').map((q, i) => {
                  const parsedMain = parseModelAnswerContent(q.modelAnswer);
                  return (
                     <div key={q.id} className="bg-gray-50 dark:bg-[#0f1221] border border-gray-200 dark:border-white/10 rounded-xl p-4 sm:p-6">
                      <div className="flex gap-4 mb-4">
                        <span className="text-muted-foreground font-bold text-lg shrink-0">Q{i + 1}</span>
                        <div className="flex-1">
                          <MarkdownText className="text-sm text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-wrap">
                            {q.text}
                          </MarkdownText>

                          {q.subQuestions && q.subQuestions.length > 0 ? (
                            <div className="space-y-6">
                              {q.subQuestions.map((sq, sqIdx) => {
                                const parsedSub = parseModelAnswerContent(sq.modelAnswer);
                                const subOption = sq.correctAnswerIndex !== undefined ? sq.options?.[sq.correctAnswerIndex] || '' : '';
                                return (
                                  <div key={sq.id} className="border-l-2 border-gray-200 dark:border-white/10 pl-4">
                                    <div className="flex justify-between items-start mb-3">
                                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100 flex gap-2">
                                        <span>{String.fromCharCode(97 + sqIdx)})</span>
                                        <MarkdownText className="whitespace-pre-wrap">{sq.text}</MarkdownText>
                                      </div>
                                      <div className="text-sm font-bold text-[#f97316] shrink-0">{sq.marks} mks</div>
                                    </div>

                                    <div className="bg-[#1a1008] border-l-4 border-[#f97316] p-4 rounded-r-lg mb-4">
                                      <div className="text-[#f97316] font-medium text-sm flex gap-2">
                                        {sq.type === 'Multiple Choice' && sq.correctAnswerIndex !== undefined && (
                                          <span className="font-bold">{String.fromCharCode(65 + sq.correctAnswerIndex)}.</span>
                                        )}
                                        <MarkdownText className="whitespace-pre-wrap">{sq.type === 'Multiple Choice' ? subOption : parsedSub.answer}</MarkdownText>
                                      </div>
                                    </div>

                                    <div className="space-y-2">
                                      {parsedSub.steps.map((step, stepIndex) => (
                                        <div key={stepIndex} className="text-[#4ade80] text-sm font-medium flex gap-2">
                                          <span className="shrink-0">Step {stepIndex + 1}:</span>
                                          <MarkdownText className="whitespace-pre-wrap">{step}</MarkdownText>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div>
                              <div className="flex justify-between items-center mb-3">
                                <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Answer</div>
                                <div className="text-sm font-bold text-[#f97316]">{q.marks} mks</div>
                              </div>

                              <div className="bg-[#1a1008] border-l-4 border-[#f97316] p-4 rounded-r-lg mb-4">
                                <div className="text-[#f97316] font-medium text-sm flex gap-2">
                                  {q.type === 'Multiple Choice' && q.correctAnswerIndex !== undefined && (
                                    <span className="font-bold">{String.fromCharCode(65 + q.correctAnswerIndex)}.</span>
                                  )}
                                  <MarkdownText className="whitespace-pre-wrap">{q.type === 'Multiple Choice' ? (q.options?.[q.correctAnswerIndex || 0] || '') : parsedMain.answer}</MarkdownText>
                                </div>
                              </div>

                              <div className="space-y-2">
                                {parsedMain.steps.map((step, stepIndex) => (
                                  <div key={stepIndex} className="text-[#4ade80] text-sm font-medium flex gap-2">
                                    <span className="shrink-0">Step {stepIndex + 1}:</span>
                                    <MarkdownText className="whitespace-pre-wrap">{step}</MarkdownText>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
               })}
            </div>
        </CardContent>
      </Card>

      {/* Configuration & Teacher Reflection */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Configuration */}
        <Card>
          <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
             <CardTitle className="flex items-center gap-2">
               <div className="w-1.5 h-4 bg-gray-900 dark:bg-white rounded-full"></div>
               Configuration
             </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 pt-6 animate-in fade-in duration-300 px-2 sm:px-6">

          <div className="space-y-3">
            <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-white/10 flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Shuffle Questions</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">RANDOMIZE ORDER FOR STUDENTS</div>
                </div>
              </div>
              <div className="w-10 h-5 bg-[#1e2330] rounded-full relative cursor-pointer">
                <div className="w-4 h-4 bg-gray-500 rounded-full absolute top-0.5 left-0.5"></div>
              </div>
            </div>

            <div className="bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-white/10 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900 dark:text-gray-100">Marking Guide</div>
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">ATTACH RUBRIC TO ASSESSMENT</div>
                </div>
              </div>
              <div className="w-10 h-5 bg-blue-600 rounded-full relative cursor-pointer">
                <div className="w-4 h-4 bg-white rounded-full absolute top-0.5 right-0.5"></div>
              </div>
            </div>
          </div>
          </CardContent>
        </Card>

        {/* Teacher Reflection */}
        <Card>
          <CardHeader className="pb-4 border-b border-gray-100 dark:border-white/5 p-2 sm:p-6">
            <CardTitle>Teacher Reflection</CardTitle>
          </CardHeader>
          <CardContent className="pt-6 px-2 sm:px-6">
          <textarea 
            placeholder="Notes: How does this assess the topic? What pre-requisite knowledge is needed?"
            className="w-full h-[120px] bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl p-4 text-sm text-muted-foreground placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors resize-none"
          ></textarea>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}